import { DateTime } from 'luxon';
import { SAPEquipment, SAPServiceContract } from './sap-rd';

export const API_DATE_FORMAT = 'yyyyMMdd';

export type ContractType = 'insight' | 'leasing' | 'maintenance' | 'rental-short-term' | 'on-demand-maintenance';

export type ProfitCenter =
    | 'ECH'
    | 'FLH'
    | 'FLL'
    | 'FLM'
    | 'FLT98'
    | 'HTT'
    | 'ISS05'
    | 'MTT'
    | 'RSS'
    | 'RTG'
    | 'SER04_R'
    | 'SER06'
    | 'SER07';

export enum MaintenanceStatus {
    CALLED = 'called',
    COMPLETED = 'completed',
    DECLINED = 'declined',
    ORDERED = 'ordered',
    SCHEDULED = 'scheduled',
}

export interface EquipmentContract {
    contract: SAPServiceContract;
    type: ContractType;
}

export interface Equipment extends SAPEquipment {
    type: string | null;
    contracts: EquipmentContract[];
    contractTypeSet: ContractType[];
    profitCenter: string;
}

export interface PreventiveMaintenanceCompletionKPINumbers {
    planned: number;
    completed: number;
    percentage: number;
}

export interface PreventiveMaintenanceCompletionKPI {
    all: PreventiveMaintenanceCompletionKPINumbers;
    byType: {
        [key: string]: PreventiveMaintenanceCompletionKPINumbers;
    };
    bySite: {
        [key: string]: {
            all: PreventiveMaintenanceCompletionKPINumbers;
            byType: {
                [key: string]: PreventiveMaintenanceCompletionKPINumbers;
            };
        };
    };
    startTime: string;
    endTime: string;
}

export interface EquipmentAvailabilityByEquipment {
    [key: string]: {
        average: number;
        totalDownTime: number;
    };
}

export interface EquipmentAvailabilityByType {
    [key: string]: {
        average: number | null;
        byEquipment: EquipmentAvailabilityByEquipment;
    };
}

export interface EquipmentAvailabilityKPI {
    average: number | null;
    bySite: {
        [key: string]: {
            average: number | null;
            byType: EquipmentAvailabilityByType | null;
        };
    } | null;
    byType: EquipmentAvailabilityByType | null;
    startTime: string;
    endTime: string;
}

export interface ResponseTimeKPICallout {
    malfunction_start: string;
    minutes: number;
    operation_description: string;
    operation_start: string;
}

export interface ResponseTimeKPI {
    average_minutes: number | null;
    callouts: ResponseTimeKPICallout[] | null;
    bySite: {
        [key: string]: {
            average_minutes: number | null;
            callouts: ResponseTimeKPICallout[] | null;
        };
    } | null;
    startTime: string;
    endTime: string;
}

export interface MaintenancePlan {
    siteNumber: string;
    equipment: {
        equipmentNumber: string;
        serialNumber: string;
        technicalIdentificationNumber: string | null;
    };
    description: string;
    date: string;
    status: MaintenanceStatus;
    serviceOrderNumber: string | null;
    hasServiceReport: boolean;
    isUnplanned: boolean;
}
export interface Components {
    description: string;
    component_serial_numbers: string[];
}
export interface SalesforceUser {
    ContactId: string | null;
}

export interface SalesforceCase {
    Id: string;
    CaseNumber: string;
    Subject: string;
    Status: string;
    Type: string | null;
    RecordTypeName?: string | null;
}

export interface Site {
    name: string;
    city: string;
    country: string | null;
    country_code: string | null;
    postal_code: string;
    ship_to_number: string;
    street_address: string;
}

export interface Contact {
    countryName: string;
    email: string;
    phone: string;
}

export enum SalesOrderStatus {
    NotConfirmed = 'NOT CONFIRMED',
    Open = 'OPEN',
    InWarehouseProcess = 'IN WAREHOUSE PROCESS',
    Shipped = 'SHIPPED',
    Delivered = 'DELIVERED',
    Cancelled = 'CANCELLED',
}
export type ShipmentTrackingLink = {
    code: string;
    url: string;
};

export type SalesOrdersRow = {
    date: string | null | undefined;
    estimatedDeliveryDate: string | null | undefined;
    proofOfDeliveryDate: string | null | undefined;
    status: SalesOrderStatus | null | undefined;
    trackingUrl: string | null | undefined;
};
export interface SalesOrder {
    documentDate: string;
    globalOrderNumber: string;
    purchaseOrderNumber: string;
    rows: SalesOrdersRow[];
    status: SalesOrderStatus;
}

export interface RunningHours {
    runningHours: number | undefined;
}

export interface Manual {
    name: string,
    type: string,
    description: string,
    file_size: number,
    file_name: string,
    url: string,
    url_expiry_time: DateTime,
    ietf_language_code: string,
    iso_language_code: string
}

// Email or Request Data Types

export enum EmailType {
    RequestMaintenance = 'REQUEST_MAINTENANCE',
    RequestTraining = 'REQUEST_TRAINING',
    RequestRegistration = 'REQUEST_REGISTRATION',
    RequestData = 'REQUEST_DATA',
}

export enum EmailStatus {
    Created = 'EMAIL_CREATED',
    Sent = 'EMAIL_SENT',
    Failed = 'EMAIL_FAILED',
    Deleted = 'EMAIL_DELETED',
    Archived = 'EMAIL_ARCHIVED',
}

export enum MaintenanceType {
    Preventive = 'Preventive Maintenance',
    Corrective = 'Corrective Maintenance',
    Breakdown = 'Breakdown Maintenance',
    Other = 'Other Maintenance',
}

export enum TrainingType {
    OperatorTraining = 'Operator Training',
    EcoDrivingTraining = 'Eco Driving Training',
    ProductTraining = 'Product Training',
    TechnicalTraining = 'Technical Training',
}

export enum ProductType {
    MyKalmar = 'MyKalmar',
    Insight = 'MyKalmar Insight',
    Store = 'MyKalmar Store',
}

export enum DataConcernedType {
    AllData = 'All data which is readily available to the Data Holder including personal Data',
    NonPersonalData = 'Only non-personal Data',
    Other = 'Other',
}
export enum DataPeriodType {
    PastData = 'Past data',
    FutureData = 'Future data',
}

export enum AccessTimingType {
    WithoutUniqueDelay = 'Without undue delay',
    Continuously = 'Continuously',
    Realtime = 'Realtime',
    WithAppropriateFrequency = 'With appropriate frequency',
    Other = 'Other',
}

export type ServiceRequest = {
    serialNumber: string;
    description: string;
    maintenanceType: MaintenanceType;
    phoneNumber: string;
    shipTo: string;
    reference: string;
    preferredMaintenanceDate: string;
}

export type TrainingRequest = {
    participants: number;
    trainingType: TrainingType;
    trainingDate: string;
    serialNumber: string;
    description: string;
}

export type RegisterRequest = {
    description: string;
}

export type DataRequest = {
    customerNumber: string;
    companyName: string;
    name: string;
    position: string;
    products: string;
    requestDate: string;
    requiredData?: string;
}

export type Email = {
    id?: string;
    userId: string;
    userName: string;
    customerNumber: string;
    customerName: string;
    senderEmail: string;
    sentToEmail?: string[];
    emailSubject: string;
    emailBodyHtml: string;
    emailBodyJson: ServiceRequest | TrainingRequest | RegisterRequest | DataRequest | null;
    type: EmailType;
    status?: EmailStatus;
    createdOn?: string;
}

export type AppSettingKey =
    'features-visibility' |
    'emails' |
    'maintenance-mode';

export interface AppSetting {
    key: AppSettingKey;
    value: JSON;
    updated_on: string;
}

// Document related types
export enum DocumentType {
    Other = "OTHER",
    Specification = "SPECIFICATION",
    Picture = "PICTURE",
    DataSheet = "DATA_SHEET"
}
export interface Document {
    id?: number;
    key: string; // link to S3
    type: DocumentType;
    name?: string;
    ietfLanguageCode?: string;
    url?: string; // signed url
    urlExpiryTime?: Date; // signed url expiry time
    createdAt?: Date;
    updatedAt?: Date;
}

// Stock Equipment related types

export enum StockEquipmentStatus {
    InActive = "INACTIVE",
    Active = "ACTIVE",
    Archived = "ARCHIVED",
    Sold = "SOLD",
    SoldArchived = "SOLD_ARCHIVED",
    Reserved = "RESERVED",
};

export enum Region {
    Asia = "Asia",
    EuropeNorth = "Europe North",
    EuropeSouthAndMEA = "Europe South and MEA",
    LatinAmerica = "Latin America",
    NorthAmerica = "North America",
    Oceania = "Oceania",
};

export enum EngineType {
    Stage3A = "Stage 3A",
    Stage5 = "Stage 5",
    Electric = "N/A (Electric)",
};

export enum MauLocation {
    Ottawa = "Ottawa",
    Shanghai = "Shanghai",
    Stargard = "Stargard",
};

export interface StockEquipment {
    id?: number;
    configurationNumber: string;
    description?: string;
    orderNumber?: string;
    quoteNumber?: string;
    range?: string;
    type?: string;
    model?: string;
    estimatedAvailability?: Date;
    documentKeys?: string[];
    comment?: string;
    addedDate?: Date;
    inventory?: number;
    soldDate?: Date | null;
    status?: StockEquipmentStatus;
    region?: Region[];
    engineType?: EngineType;
    mauLocation?: MauLocation;
    manufacturingYear?: number;
    price?: number | null;
    currency?: string | null;
    contactEmail?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface PreventiveMaintenanceComment {
    operation: string;
    checkpoint: string;
    comment: string;
    status: string;
}

export interface PreventiveMaintenanceRecord {
    serviceOrderNumber: string;
    status: string;
    maintenanceInterval: string; // e.g. "500h"
    date: string; 
    faults: number;
    commentsCount: number;
    comments: PreventiveMaintenanceComment[];
    reportDownloadUrl: string | null;
}

export interface BulletinFile {
    driveFileId: string;
    language: string;
}

export interface Bulletin {
    fileName: string;
    product?: string;
    subject?: string;
    type?: string;
    date?: string;
    files: BulletinFile[];
}